import { FC, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '@components/layout/DashboardLayout';
import { PropertyTable } from '@components/properties/PropertyTable';
import { PropertyFormWizard } from '@components/properties/PropertyFormWizard';
import { Modal } from '@components/feedback/Modal';
import { Button } from '@components/common/Button';
import { Property, PropertyStatus, PortfolioPropertyFormData } from '@/types/index';
import { propertyService } from '@services/property.service';

export const MyProperties: FC = () => {
  const navigate = useNavigate();
  const [properties, setProperties] = useState<Property[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isWizardOpen, setIsWizardOpen] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState<Property | undefined>(
    undefined,
  );

  // Charger mes propriétés
  useEffect(() => {
    loadProperties();
  }, []);

  const loadProperties = async () => {
    try {
      setIsLoading(true);
      const data = await propertyService.getAllFiltered({ status: PropertyStatus.PURCHASED });
      setProperties(data.properties);
    } catch (error) {
      console.error('Erreur lors du chargement des propriétés:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddProperty = () => {
    setSelectedProperty(undefined);
    setIsWizardOpen(true);
  };

  const handleEditProperty = async (property: Property) => {
    try {
      const freshProperty = await propertyService.getById(property.id);
      setSelectedProperty(freshProperty);
    } catch (error) {
      console.error('Erreur chargement propriété:', error);
      setSelectedProperty(property);
    }
    setIsWizardOpen(true);
  };

  const handleDeleteProperty = async (id: string) => {
    try {
      await propertyService.delete(id);
      await loadProperties();
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      alert('Erreur lors de la suppression de la propriété');
    }
  };

  const handleWizardSuccess = () => {
    setIsWizardOpen(false);
    setSelectedProperty(undefined);
    loadProperties();
  };

  const handleWizardCancel = () => {
    setIsWizardOpen(false);
    setSelectedProperty(undefined);
    loadProperties();
  };

  const handleAddToPortfolio = (property: Property) => {
    const prefill: PortfolioPropertyFormData = {
      name: property.address ? `${property.address}, ${property.city}` : property.city,
      propertyId: property.id,
      address: property.address || undefined,
      city: property.city,
      postalCode: property.postalCode,
      canton: property.canton,
      area: property.area,
      rooms: property.rooms,
      acquisitionPrice: property.askingPrice,
      // loyer : réel si loué, sinon estimation — les deux sont mensuels → ×12
      grossAnnualRent: property.isRented && property.rentPrice
        ? property.rentPrice * 12
        : property.estimatedRent
          ? property.estimatedRent * 12
          : undefined,
      // charges PPE mensuelles → annuelles
      ppeChargesAnnual: property.ppeCharges ? property.ppeCharges * 12 : undefined,
    };
    navigate('/portfolio', { state: { prefillFromProperty: prefill } });
  };

  return (
    <DashboardLayout title="Mes propriétés">
      <div className="max-w-7xl mx-auto p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
            Mes propriétés
          </h1>
          <Button onClick={handleAddProperty} variant="primary">
            + Ajouter une propriété
          </Button>
        </div>

        {/* Lien vers le portefeuille */}
        <div className="flex items-center gap-3 p-4 rounded-xl bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
          <svg className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-sm text-blue-700 dark:text-blue-300 flex-1">
            Pour piloter vos actifs financièrement (cash-flow, LTV, rendement), utilisez le{' '}
            <button onClick={() => navigate('/portfolio')} className="font-semibold underline hover:no-underline">
              module Portefeuille
            </button>.
            Cliquez sur 📊 dans le tableau pour ajouter un bien directement dans le portefeuille avec les données pré-remplies.
          </p>
        </div>

        {/* Tableau */}
        <PropertyTable
          properties={properties}
          onEdit={handleEditProperty}
          onDelete={handleDeleteProperty}
          onAddToPortfolio={handleAddToPortfolio}
          isLoading={isLoading}
        />

        {/* Modal Wizard */}
        <Modal
          isOpen={isWizardOpen}
          onClose={handleWizardCancel}
          title={
            selectedProperty ? 'Modifier la propriété' : 'Ajouter une propriété'
          }
          size="xl"
        >
          <PropertyFormWizard
            property={selectedProperty}
            onSuccess={handleWizardSuccess}
            onCancel={handleWizardCancel}
            onSave={loadProperties}
          />
        </Modal>
      </div>
    </DashboardLayout>
  );
};
